import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  HStack,
  InputRightElement,
  Stack,
  Button,
  Heading,
  Text,
  useColorModeValue,
  Link,
  Select,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import axios from 'axios';

const SignupForm = ({ onSignup }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    number: '',
    username: '',
    password: '',
    user_type: '',
    street_address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US', // We are only working with US now
    camera: '',
    equipment: '',
    photography_style: ''
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullAddress = `${formData.street_address}, ${formData.city}, ${formData.state}, ${formData.zip}, ${formData.country}`;
    try {
      const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
        params: {
          address: fullAddress,
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
        }
      });
      const location = response.data.results[0].geometry.location;
      const updatedFormData = { ...formData, latitude: location.lat, longitude: location.lng };

      const backendUrl = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_BACKEND_URL
  : 'http://localhost:8080';
      const signupResponse = await axios.post(`${backendUrl}/signup`, updatedFormData, {
        headers: {
          'Content-Type': 'application/json'
        },
      });

      onSignup(updatedFormData);
      if (formData.user_type === 'customer') {
        navigate('/login');
      } else {
        navigate('/user');
      }
    } catch (error) {
      console.error('Error signing up:', error);
    }
  };

  const bgColor = useColorModeValue('white', 'black');
  const textColor = useColorModeValue('black', 'white');
  const headingColor = useColorModeValue('black', 'white');

  return (
    <Flex
      minH={'100vh'}
      align={'center'}
      justify={'center'}
      bg={bgColor}>
      <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6}>
        <Stack align={'center'}>
          <Heading fontSize={'4xl'} textAlign={'center'}>
            Sign up
          </Heading>
          <Text fontSize={'lg'} color={'gray.600'}>
            Join Papirazzi today!
          </Text>
        </Stack>
        <Box
          rounded={'lg'}
          //bg={useColorModeValue('white', 'gray.900')}
          bg={bgColor}
          boxShadow={'lg'}
          p={8}>
          <Stack spacing={4} as="form" onSubmit={handleSubmit}>
            <HStack>
              <Box>
                <FormControl id="first_name" isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input type="text" name="first_name" value={formData.first_name} onChange={handleChange} />
                </FormControl>
              </Box>
              <Box>
                <FormControl id="last_name" isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input type="text" name="last_name" value={formData.last_name} onChange={handleChange} />
                </FormControl>
              </Box>
            </HStack>
            <FormControl id="email" isRequired>
              <FormLabel>Email address</FormLabel>
              <Input type="email" name="email" value={formData.email} onChange={handleChange} />
            </FormControl>
            <FormControl id="number" isRequired>
              <FormLabel>Number</FormLabel>
              <Input type="number" name="number" value={formData.number} onChange={handleChange} />
            </FormControl>
            <FormControl id="username" isRequired>
              <FormLabel>Username</FormLabel>
              <Input type="text" name="username" value={formData.username} onChange={handleChange} />
            </FormControl>
            <FormControl id="password" isRequired>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} />
                <InputRightElement h={'full'}>
                  <Button
                    variant={'ghost'}
                    onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            {/* Address Fields */}
            <FormControl id="street_address" isRequired>
              <FormLabel>Street Address</FormLabel>
              <Input
                type="text"
                name="street_address"
                value={formData.street_address}
                onChange={handleChange}
              />
            </FormControl>

            {/* City Field */}
            <FormControl id="city" isRequired mt={4}>
              <FormLabel>City</FormLabel>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </FormControl>

            {/* State Field */}
            <FormControl id="state" isRequired mt={4}>
              <FormLabel>State</FormLabel>
              <Input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </FormControl>

            {/* Zip Field */}
            <FormControl id="zip" isRequired mt={4}>
              <FormLabel>Zip</FormLabel>
              <Input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
              />
            </FormControl>

              {/*<Box>
                <FormControl id="country" isRequired>
                  <FormLabel>Country</FormLabel>
                  <Input type="text" name="country" value={formData.country} onChange={handleChange} />
                </FormControl>
              </Box>*/}

            <FormControl id="user_type" isRequired>
              <FormLabel>User Type</FormLabel>
              <Select name="user_type" value={formData.user_type} onChange={handleChange}>
                <option value="">Select user type</option>
                <option value="photographer">Photographer</option>
                <option value="customer">Customer</option>
              </Select>
            </FormControl>

            {/* Photographer-specific fields */}
            {formData.user_type === 'photographer' && (
              <>
                <FormControl id="camera">
                  <FormLabel>Camera</FormLabel>
                  <Input type="text" name="camera" value={formData.camera} onChange={handleChange} />
                </FormControl>
                <FormControl id="equipment">
                  <FormLabel>Equipment</FormLabel>
                  <Input type="text" name="equipment" value={formData.equipment} onChange={handleChange} />
                </FormControl>
                <FormControl id="photography_style">
                  <FormLabel>Photography Style</FormLabel>
                  <Input type="text" name="photography_style" value={formData.photography_style} onChange={handleChange} />
                </FormControl>
              </>
            )}

            <Stack spacing={10} pt={2}>
              <Button
                type="submit"
                size="lg"
                bg={'blue.400'}
                color={'white'}
                _hover={{
                  bg: 'blue.500',
                }}>
                Sign up
              </Button>
            </Stack>
            <Stack pt={6}>
              <Text align={'center'}>
                Already a user? <Link color={'blue.400'} href='/login'>Login</Link>
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Flex>
  );
};

export default SignupForm;
